/**
 * NavigationGuard — صفحات dirty خود را ثبت می‌کنند؛ MainLayout قبل از navigate می‌پرسد.
 */
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Modal, Button, Space } from "antd";
import Alert from "../components/GxAlert";

const NavGuardCtx = createContext(null);

export function NavigationGuardProvider({ children }) {
  const handlerRef = useRef(null); // { isDirty, canFinalize, onSaveDraft, onFinalize, label }
  const [modal, setModal] = useState(null); // { to, resolve }
  const [busy, setBusy] = useState(false);

  const register = useCallback((handler) => {
    handlerRef.current = handler;
    return () => {
      if (handlerRef.current === handler) handlerRef.current = null;
    };
  }, []);

  const tryNavigate = useCallback((to, doNavigate) => {
    const h = handlerRef.current;
    const dirty = h && typeof h.isDirty === "function" ? h.isDirty() : false;
    if (!dirty) {
      doNavigate(to);
      return;
    }
    // یک Modal هم‌زمان
    setModal((prev) => {
      if (prev) return prev;
      return { to, doNavigate };
    });
  }, []);

  const closeModal = () => setModal(null);

  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      const ok = await fn();
      if (ok) {
        setModal((m) => {
          if (m) m.doNavigate(m.to);
          return null;
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const value = useMemo(() => ({ register, tryNavigate }), [register, tryNavigate]);
  const h = modal ? handlerRef.current : null;
  const canFinalize = h && typeof h.canFinalize === "function" ? h.canFinalize() : { ok: false, reason: "" };

  return (
    <NavGuardCtx.Provider value={value}>
      {children}
      <Modal
        open={!!modal}
        title="اطلاعات این فاکتور هنوز ذخیره نشده است"
        onCancel={() => { if (!busy) closeModal(); }}
        footer={null}
        keyboard={!busy}
        maskClosable={false}
        destroyOnClose
      >
        <p style={{ marginBottom: 16 }}>می‌خواهید قبل از خروج چه کاری انجام شود؟</p>
        {!canFinalize.ok && canFinalize.reason && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={canFinalize.reason} />
        )}
        <Space direction="vertical" style={{ width: "100%" }} size={8}>
          <Button type="primary" block loading={busy}
            onClick={() => run(async () => {
              if (h?.onSaveDraft) return h.onSaveDraft();
              return true;
            })}>
            ذخیره پیش‌نویس و ادامه
          </Button>
          <Button block loading={busy} disabled={!canFinalize.ok}
            onClick={() => run(async () => {
              if (h?.onFinalize) return h.onFinalize();
              return false;
            })}>
            تکمیل و ثبت نهایی و ادامه
          </Button>
          <Button danger block disabled={busy}
            onClick={() => run(async () => {
              if (h?.onDiscard) h.onDiscard();
              return true;
            })}>
            خروج بدون ذخیره
          </Button>
          <Button block disabled={busy} onClick={closeModal}>انصراف</Button>
        </Space>
      </Modal>
    </NavGuardCtx.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavGuardCtx);
  if (!ctx) {
    return {
      register: () => () => {},
      tryNavigate: (to, doNav) => doNav(to),
    };
  }
  return ctx;
}
